import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComputerAssetViewerContext, ComputerAssetViewerHost, getAssetDisplayName, } from '#pplx/frontend/apps/ask/computer/asset-viewer/ComputerAssetViewerContextShared.js';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
export function ComputerAssetViewerProvider({ children, resetKey, }) {
    const existing = useContext(ComputerAssetViewerContext);
    // Standalone Computer surfaces still need an owner; SPA routes reuse the tab's viewer.
    return existing ? (children) : (_jsx(ComputerAssetViewerOwner, { resetKey: resetKey, children: children }));
}
function ComputerAssetViewerOwner({ children, resetKey, }) {
    const [viewerRequest, setViewerRequest] = useState(null);
    const [isHostOpen, setIsHostOpen] = useState(false);
    const [openInfo, setOpenInfo] = useState(null);
    const [allSharedAssets, setAllSharedAssets] = useState([]);
    const [pageViewer, setPageViewer] = useState(null);
    const pageViewerRef = useRef(null);
    const previousResetKey = useRef(resetKey);
    const updateOpenInfo = useCallback((asset) => {
        setOpenInfo({
            name: getAssetDisplayName(asset),
            type: asset.asset_type ?? 'UNKNOWN',
            uuid: asset.uuid ?? '',
        });
    }, []);
    const openAsset = useCallback((asset) => {
        updateOpenInfo(asset);
        setIsHostOpen(true);
        setViewerRequest((request) => ({
            asset,
            requestId: (request?.requestId ?? 0) + 1,
        }));
    }, [updateOpenInfo]);
    const closeAsset = useCallback(() => {
        setViewerRequest(null);
        setIsHostOpen(false);
        setOpenInfo(null);
    }, []);
    const isOpen = viewerRequest !== null || isHostOpen;
    useLayoutEffect(() => {
        if (previousResetKey.current === resetKey)
            return;
        previousResetKey.current = resetKey;
        // Reset the viewer before painting a new account or route, without remounting the app.
        closeAsset();
        setAllSharedAssets([]);
        pageViewerRef.current?.closeAsset();
    }, [resetKey, closeAsset]);
    const registerPageViewer = useCallback((viewer) => {
        closeAsset();
        pageViewerRef.current = viewer;
        setPageViewer(viewer);
        return () => {
            if (pageViewerRef.current === viewer)
                pageViewerRef.current = null;
            setPageViewer((current) => (current === viewer ? null : current));
        };
    }, [closeAsset]);
    const value = useMemo(() => ({
        openAsset,
        closeAsset,
        isOpen,
        openAssetInfo: isOpen ? openInfo : null,
        ...pageViewer,
        setAllSharedAssets,
        registerPageViewer,
    }), [openAsset, closeAsset, isOpen, openInfo, pageViewer, setAllSharedAssets, registerPageViewer]);
    return (_jsxs(ComputerAssetViewerContext.Provider, { value: value, children: [children, !pageViewer && viewerRequest && (_jsx(ComputerAssetViewerHost, { allAssets: allSharedAssets, asset: viewerRequest.asset, onActiveAssetChange: updateOpenInfo, onClose: closeAsset, onOpenChange: setIsHostOpen, requestId: viewerRequest.requestId }, viewerRequest.requestId))] }));
}
//# sourceMappingURL=ComputerAssetViewerContext.js.map